import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { ago, money, moneyShort, time } from "@/lib/format";
import { METHOD_LABEL, type Item } from "@/lib/types";
import { Button, cx, useConfirm, useToast } from "@/components/ui";
import { useSession } from "@/lib/session";
import { atLeast } from "@/lib/types";
import { providerLabel } from "@/lib/paystack";
import { Card, PageHead, td, th } from "@/components/Layout";
import { Overview, PeriodSwitch, periodRange, useInsights } from "@/components/Insights";
import { useShop } from "@/lib/till-context";

type Dash = {
  unchecked: { sale: string; saleId: string; method: string; amount: number; ref: string | null; staff: string; at: number }[];
  takings: number;
  sales: number;
  devices: number;
  attachRate: number | null;
  refunds: number;
  refundCount: number;
  hours: Record<string, number>;
  categories: { name: string; amount: number }[];
  methods: Record<string, number>;
  cashiers: { name: string; sales: number; takings: number; devices: number; discounts: number; tills: string[] }[];
  low: { id: string; name: string; onHand: number; reorderAt: number; web: number | null; linked: boolean }[];
  tills: { id: string; name: string; lastSeenAt: number | null; shift: { staffName: string; openedAt: number } | null }[];
};

type Unmatched = { id: string; reference: string; saleId: string; saleNumber: string | null; method: string; channel: string; amount: number; phone: string | null; provider: string | null; paidWith: string | null; createdAt: number };

/** Money Paystack took that no sale used: refund it through Paystack, or note how it was settled. */
function UnmatchedList({ list }: { list: Unmatched[] }) {
  const s = useSession();
  const manager = atLeast(s.staff?.role, "manager");
  const confirm = useConfirm();
  const toast = useToast();
  const qc = useQueryClient();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const act = async (p: Unmatched, kind: "refund" | "settle") => {
    if (kind === "refund" && !(await confirm({ title: `Refund ${money(p.amount)} through Paystack?`, body: `It goes back to ${p.paidWith || "the customer's account"}${p.phone ? ` (${p.phone})` : ""}. Paystack takes a few days to complete it.`, ok: "Refund", danger: true }))) return;
    setBusy(p.id);
    try {
      await api(till(`/admin/unmatched/${encodeURIComponent(p.id)}/${kind}`), { body: { note: notes[p.id] ?? "" } });
      toast(kind === "refund" ? "Refund started on Paystack" : "Marked as dealt with");
      void qc.invalidateQueries({ queryKey: ["unmatched"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "That didn't work", "err");
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card title="Paid in Paystack, not on any sale" className="scroll-mt-4" >
      <div id="unmatched" className="flex flex-col px-5 pb-4">
        <p className="pb-3 text-[13px] text-slate-500">A customer approved a payment after the cashier moved on, paid an old QR or link, or paid twice. {manager ? "Refund it, or note what was done (e.g. put on a later sale by hand)." : "A manager can refund it or mark it dealt with."}</p>
        {list.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-3 border-t border-slate-200 py-3">
            <span className="flex min-w-[220px] flex-1 flex-col">
              <b className="font-mono text-[15px]">{money(p.amount)} <span className="font-sans text-[13px] font-semibold text-slate-500">{p.paidWith || (p.method === "card" ? "Card" : `${providerLabel(p.provider) || "Mobile"} MoMo`)}{p.phone ? ` · ${p.phone}` : ""}</span></b>
              <span className="text-xs text-slate-500">{new Date(p.createdAt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {p.reference}{p.saleNumber ? ` · sale ${p.saleNumber} was paid another way` : " · the sale was never completed"}</span>
            </span>
            {manager ? (
              <>
                <input value={notes[p.id] ?? ""} onChange={(e) => setNotes({ ...notes, [p.id]: e.target.value })} placeholder="Note (what was done)" aria-label="Note" className="h-10 w-56 border border-slate-300 px-2.5 text-sm" />
                <Button disabled={busy === p.id || (notes[p.id] ?? "").trim().length < 4} onClick={() => void act(p, "settle")}>Mark dealt with</Button>
                <Button tone="dark" disabled={busy === p.id} onClick={() => void act(p, "refund")}>Refund</Button>
              </>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function rangeFor(r: string) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (r === "week") start.setDate(start.getDate() - 6);
  if (r === "month") start.setDate(start.getDate() - 29);
  return { from: start.getTime(), to: Date.now() };
}

export function Dashboard() {
  const [r, setR] = useState<string>("today");
  const shop = useShop();
  const q = useQuery({ queryKey: ["dash", r], queryFn: () => { const { from, to } = periodRange(r); return api<Dash>(till(`/admin/dashboard?from=${from}&to=${to}`)); }, refetchInterval: 60_000 });
  const ins = useInsights(r);
  const d = q.data;
  const hours = Array.from({ length: 13 }, (_, i) => i + 8);
  const maxH = Math.max(1, ...hours.map((h) => d?.hours[h] ?? 0));
  const catTotal = Math.max(1, (d?.categories ?? []).reduce((n, c) => n + c.amount, 0));
  const items = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: Item[] }>(till("/admin/items")), staleTime: 60_000 });
  const unmatched = useQuery({ queryKey: ["unmatched"], queryFn: () => api<{ payments: Unmatched[] }>(till("/admin/unmatched")), refetchInterval: 120_000 });
  const lost = unmatched.data?.payments ?? [];
  const notIn = (items.data?.items ?? []).filter((i) => i.active && !!i.webSlug && i.tracking !== "service" && (i.onHand ?? 0) <= 0);
  const tasks = [
    notIn.length ? { t: `${notIn.length} imported product${notIn.length === 1 ? " has" : "s have"} no stock in the shop`, s: 'They show "Not in shop" at the till. Bring units across from the website.', to: "/products?filter=notin", cta: "Bring stock in", warn: true } : null,
    d && d.low.length ? { t: `${d.low.length} product${d.low.length === 1 ? " is" : "s are"} running low`, s: d.low.slice(0, 2).map((l) => `${l.name} (${l.onHand})`).join(", ") + (d.low.length > 2 ? ` and ${d.low.length - 2} more` : ""), to: "/stock", cta: "Receive", warn: false } : null,
    d && d.unchecked?.length ? { t: `${d.unchecked.length} MoMo / card payment${d.unchecked.length === 1 ? "" : "s"} recorded by hand while offline`, s: `${money(d.unchecked.reduce((n, u) => n + u.amount, 0))}, no supervisor approved them: ${d.unchecked.slice(0, 3).map((u) => `${u.sale} (${u.staff})`).join(", ")}${d.unchecked.length > 3 ? "…" : ""}. Check each against Paystack.`, to: "/reports", cta: "See sales", warn: true } : null,
    lost.length ? { t: `${lost.length} Paystack payment${lost.length === 1 ? " isn't" : "s aren't"} on any sale`, s: `${money(lost.reduce((n, p) => n + p.amount, 0))} paid in Paystack but on no receipt. Refund it or note what was done.`, to: "#unmatched", cta: "Review", warn: true } : null,
  ].filter(Boolean) as { t: string; s: string; to: string; cta: string; warn: boolean }[];
  return (
    <>
      <PageHead title={r === "today" ? "Today at the shop" : "The shop"} sub={new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}>
        <PeriodSwitch value={r} onChange={setR} />
      </PageHead>
      {!d ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="flex flex-col gap-4">
          {tasks.length ? (
            <section className="flex flex-col bg-ink px-5 py-4 text-white">
              <h2 className="mb-2 text-lg font-extrabold">Needs you <span className="font-mono text-sm text-[#ffb36b]">{tasks.length}</span></h2>
              {tasks.map((t) => (
                <div key={t.t} className="flex flex-wrap items-center gap-3 border-t border-steel-2 py-3">
                  <span className={cx("h-2.5 w-2.5 flex-none", t.warn ? "bg-warn" : "bg-brand-light")} />
                  <span className="flex min-w-[240px] flex-1 flex-col"><b className="text-[15px]">{t.t}</b><span className="text-[13px] text-white/60">{t.s}</span></span>
                  <Link to={t.to} className={cx("inline-flex h-11 items-center px-4 text-[13px] font-extrabold text-ink", t.warn ? "bg-warn" : "bg-brand-light")}>{t.cta}</Link>
                </div>
              ))}
            </section>
          ) : null}
          {lost.length ? <UnmatchedList list={lost} /> : null}
          {ins.data ? <Overview d={ins.data} /> : <p className="text-sm text-slate-500">{ins.isError ? "Couldn't load the period's numbers" : "Loading the period's numbers…"}</p>}
          <div className={cx("grid grid-cols-2 gap-3 xl:grid-cols-4", !d.devices && "hidden")}>
            {[
              ["Devices sold", String(d.devices), "phones, laptops, TVs by IMEI / serial"],
              ["Accessory attach", d.attachRate == null ? "-" : `${d.attachRate}%`, "device sales with an accessory added"],
              ["Refunds", moneyShort(d.refunds), `${d.refundCount} return${d.refundCount === 1 ? "" : "s"}`],
            ].map(([l, v, s]) => (
              <div key={l} className="flex flex-col gap-1.5 border border-slate-200 bg-white p-4">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">{l}</span>
                <span className="font-mono text-2xl font-bold sm:text-[27px]">{v}</span>
                <span className="text-xs text-slate-500">{s}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-4">
            <Card title="Sales by hour" className="min-w-0 flex-[2_1_480px]">
              <div className="px-5 pb-4">
                <div className="flex h-44 items-end gap-2 border-b border-slate-200">
                  {hours.map((h) => {
                    const v = d.hours[h] ?? 0;
                    const pct = (v / maxH) * 100;
                    return <div key={h} title={money(v)} className={cx("flex-1", pct >= 70 ? "bg-brand" : "bg-brand/40")} style={{ height: `${Math.max(v ? 3 : 0, pct)}%` }} />;
                  })}
                </div>
                <div className="mt-1.5 flex gap-2 font-mono text-[11px] text-slate-500">
                  {hours.map((h) => (
                    <span key={h} className="flex-1 text-center">
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            </Card>
            <Card title="By category" className="min-w-0 flex-[1_1_300px]">
              <div className="flex flex-col gap-3 px-5 pb-5">
                {d.categories.slice(0, 6).map((c, i) => (
                  <div key={c.name} className="flex flex-col gap-1.5">
                    <div className="flex justify-between text-sm">
                      <span>{c.name}</span>
                      <span className="font-mono">{moneyShort(c.amount)}</span>
                    </div>
                    <div className="h-2.5 bg-slate-100">
                      <div className={cx("h-2.5", ["bg-brand", "bg-slate-900", "bg-blue-700", "bg-amber-500"][i % 4])} style={{ width: `${(c.amount / catTotal) * 100}%` }} />
                    </div>
                  </div>
                ))}
                {!d.categories.length ? <span className="text-sm text-slate-500">No sales yet.</span> : null}
                {Object.keys(d.methods).length ? (
                  <div className="mt-2 border-t border-slate-100 pt-3 text-[13px] text-slate-600">
                    {Object.entries(d.methods).map(([m, v]) => (
                      <div key={m} className="flex justify-between">
                        <span>{METHOD_LABEL[m as keyof typeof METHOD_LABEL] ?? m}</span>
                        <span className="font-mono">{moneyShort(v)}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </Card>
          </div>
          <div className="flex flex-wrap gap-4">
            <Card title="By cashier" className="min-w-0 flex-[1_1_420px]">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      {["Cashier", "Till", "Sales", "Takings", ...(shop.serials ? ["Devices"] : []), "Discounts"].map((h) => (
                        <th key={h} className={th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.cashiers.map((c) => (
                      <tr key={c.name}>
                        <td className={cx(td, "font-semibold")}>{c.name}</td>
                        <td className={td}>{c.tills.join(", ")}</td>
                        <td className={cx(td, "font-mono")}>{c.sales}</td>
                        <td className={cx(td, "font-mono")}>{moneyShort(c.takings)}</td>
                        {shop.serials ? <td className={cx(td, "font-mono")}>{c.devices}</td> : null}
                        <td className={cx(td, "font-mono")}>{moneyShort(c.discounts)}</td>
                      </tr>
                    ))}
                    {!d.cashiers.length ? (
                      <tr>
                        <td colSpan={6} className={cx(td, "text-slate-500")}>
                          No sales yet.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </Card>
            <Card title="Running low in the shop" right={<Link to="/products" className="text-[13px] font-semibold text-brand">All stock</Link>} className="min-w-0 flex-[1_1_420px]">
              {d.low.map((l) => (
                <div key={l.id} className="flex flex-wrap items-center gap-2.5 border-t border-slate-50 px-5 py-3">
                  <span className="min-w-[160px] flex-1">
                    <b className="text-sm">{l.name}</b>
                    <span className="block text-xs text-slate-500">
                      Shop {l.onHand} · reorder at {l.reorderAt}
                      {l.linked ? ` · website has ${l.web ?? "unlimited"}` : " · shop only"}
                    </span>
                  </span>
                  <Link to={`/stock?item=${l.id}${l.linked ? "&tab=transfer" : ""}`} className="border border-slate-200 px-3 py-2 text-[13px] font-semibold">
                    {l.linked ? "Transfer from website" : "Receive"}
                  </Link>
                </div>
              ))}
              {!d.low.length ? <p className="px-5 pb-5 text-sm text-slate-500">Nothing below its reorder level.</p> : null}
            </Card>
          </div>
          <Card>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-5 py-4">
              <b className="mr-2">Tills now</b>
              {d.tills.map((t) => {
                const live = t.lastSeenAt && Date.now() - t.lastSeenAt < 5 * 60_000;
                return (
                  <span key={t.id} className="flex items-center gap-2 text-sm">
                    <span className={cx("h-2 w-2", live ? "bg-brand" : "bg-amber-500")} />
                    {t.name} · {t.shift ? `${t.shift.staffName} since ${time(t.shift.openedAt)}` : "no shift"} · {live ? "online" : `seen ${ago(t.lastSeenAt)}`}
                  </span>
                );
              })}
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
